import is from '@sindresorhus/is';
import type { Location } from 'react-router';
import { z } from 'zod';

import {
    serializeUrlTilePayloads,
    URL_TILE_SEARCH_PARAM,
    type UrlTileDefinition,
    type UrlTileLegacyRewriter,
} from '../../url-tiles';
import {
    JSA_ARCHIVE_TILE_TYPE,
    JSA_CHANGELOG_TILE_TYPE,
    JSA_CLUSTER_DETAIL_TILE_TYPE,
    JSA_COMPARE_TILE_TYPE,
    JSA_FAMILY_DETAIL_TILE_TYPE,
    JSA_PROFILE_DETAIL_TILE_TYPE,
    type JsaArchiveTilePayload,
    type JsaChangelogTilePayload,
    type JsaCompareTilePayload,
    type JsaPrimaryDetailTilePayload,
    jsaArchiveTileSchema,
    jsaChangelogTileSchema,
    jsaClusterDetailTileSchema,
    jsaCompareTileSchema,
    jsaFamilyDetailTileSchema,
    jsaProfileDetailTileSchema,
} from '../url-tile-payloads';

type JsaLegacyTilePayload =
    | JsaArchiveTilePayload
    | JsaChangelogTilePayload
    | JsaCompareTilePayload
    | JsaPrimaryDetailTilePayload;

// LEGACY: legacy URLs carry the architecture only in the `/job-architecture-v2/:architectureId`
// path, so this rewriter reads it from there and stamps it into every tile payload it produces.
// Canonical (non-legacy) tiles always carry `architectureId` in their own `tile=` search param,
// so each tile self-scopes and no ambient architecture is needed.
const architectureIdFromLocation = (location: Location): string | undefined => {
    const match = location.pathname.match(/^\/job-architecture-v2\/([^/]+)/);
    const architectureId = match?.[1];

    return z.uuid().safeParse(architectureId).success ? architectureId : undefined;
};

const getVersion = (searchParams: URLSearchParams): number => {
    const detailsVersion = searchParams.get('details.version');

    return is.nonEmptyString(detailsVersion) ? Number.parseInt(detailsVersion, 10) : 1;
};

const isLegacyTilePayload = (payload: JsaLegacyTilePayload | undefined): payload is JsaLegacyTilePayload =>
    !is.undefined(payload);

const jsaLegacySearchParamKeys = [
    'details',
    'details.profileId',
    'details.clusterId',
    'details.type',
    'details.mode',
    'details.familyId',
    'details.isArchived',
    'details.version',
    'details.preferredLanguage',
    'details.changelog',
    'details.archive',
    'comparing.version_a',
    'comparing.version_b',
    'comparing.id',
    'comparing.type',
    'comparing.detailType',
    'comparing.preferredLanguage',
] as const;

const hasJsaLegacySearchParams = (searchParams: URLSearchParams): boolean => {
    return jsaLegacySearchParamKeys.some((key) => searchParams.has(key));
};

const deleteJsaLegacySearchParams = (searchParams: URLSearchParams): void => {
    for (const key of jsaLegacySearchParamKeys) {
        searchParams.delete(key);
    }
};

const parseLegacyPrimaryPayload = (
    searchParams: URLSearchParams,
    architectureId: string,
): JsaPrimaryDetailTilePayload | undefined => {
    const detailType = searchParams.get('details');
    const mode = searchParams.get('details.mode') ?? 'view';

    if (detailType === 'job-profile') {
        const profilePayload = jsaProfileDetailTileSchema.safeParse({
            architectureId,
            isArchived: searchParams.get('details.isArchived') === 'true' ? true : undefined,
            itemType: searchParams.get('details.type'),
            mode,
            preferredLanguage: searchParams.get('details.preferredLanguage') ?? undefined,
            profileId: searchParams.get('details.profileId'),
            type: JSA_PROFILE_DETAIL_TILE_TYPE,
            version: getVersion(searchParams),
        });

        return profilePayload.success ? profilePayload.data : undefined;
    }

    if (detailType === 'family') {
        const familyPayload = jsaFamilyDetailTileSchema.safeParse({
            architectureId,
            familyId: searchParams.get('details.familyId'),
            mode,
            type: JSA_FAMILY_DETAIL_TILE_TYPE,
        });

        return familyPayload.success ? familyPayload.data : undefined;
    }

    if (detailType === 'cluster') {
        const clusterPayload = jsaClusterDetailTileSchema.safeParse({
            architectureId,
            clusterId: searchParams.get('details.clusterId'),
            familyId: searchParams.get('details.familyId'),
            mode,
            type: JSA_CLUSTER_DETAIL_TILE_TYPE,
        });

        return clusterPayload.success ? clusterPayload.data : undefined;
    }

    return undefined;
};

const parseLegacyComparePayload = (
    searchParams: URLSearchParams,
    architectureId: string,
): JsaCompareTilePayload | undefined => {
    const comparePayload = jsaCompareTileSchema.safeParse({
        architectureId,
        itemType: searchParams.get('comparing.type'),
        preferredLanguage: searchParams.get('comparing.preferredLanguage') ?? undefined,
        profileId: searchParams.get('comparing.id'),
        type: JSA_COMPARE_TILE_TYPE,
        versionA: searchParams.get('comparing.version_a'),
        versionB: searchParams.get('comparing.version_b'),
    });

    return comparePayload.success ? comparePayload.data : undefined;
};

const parseLegacyChangelogPayload = (
    searchParams: URLSearchParams,
    architectureId: string,
): JsaChangelogTilePayload | undefined => {
    const changelogPayload = jsaChangelogTileSchema.safeParse({
        architectureId,
        type: searchParams.get('details.changelog') === 'changelog' ? JSA_CHANGELOG_TILE_TYPE : undefined,
    });

    return changelogPayload.success ? changelogPayload.data : undefined;
};

const parseLegacyArchivePayload = (
    searchParams: URLSearchParams,
    architectureId: string,
): JsaArchiveTilePayload | undefined => {
    const archivePayload = jsaArchiveTileSchema.safeParse({
        architectureId,
        type: searchParams.get('details.archive') === 'archive' ? JSA_ARCHIVE_TILE_TYPE : undefined,
    });

    return archivePayload.success ? archivePayload.data : undefined;
};

const parseLegacyPayloads = (
    searchParams: URLSearchParams,
    architectureId: string,
): readonly JsaLegacyTilePayload[] => {
    return [
        parseLegacyPrimaryPayload(searchParams, architectureId),
        parseLegacyComparePayload(searchParams, architectureId),
        parseLegacyChangelogPayload(searchParams, architectureId),
        parseLegacyArchivePayload(searchParams, architectureId),
    ].filter(isLegacyTilePayload);
};

const appendSerializedLegacyPayloads = (
    searchParams: URLSearchParams,
    payloads: readonly JsaLegacyTilePayload[],
    definitions: readonly UrlTileDefinition[],
): void => {
    for (const value of serializeUrlTilePayloads(payloads, definitions)) {
        searchParams.append(URL_TILE_SEARCH_PARAM, value);
    }
};

export const jobArchitectureLegacyUrlTileRewriter: UrlTileLegacyRewriter = {
    rewrite: ({ definitions, hadCanonicalTileParams, location, searchParams }) => {
        const architectureId = architectureIdFromLocation(location);

        if (is.undefined(architectureId) || !hasJsaLegacySearchParams(searchParams)) {
            return undefined;
        }

        const nextParams = new URLSearchParams(searchParams);
        deleteJsaLegacySearchParams(nextParams);

        if (hadCanonicalTileParams) {
            return nextParams;
        }

        appendSerializedLegacyPayloads(nextParams, parseLegacyPayloads(searchParams, architectureId), definitions);

        return nextParams;
    },
};
